import {
  memo,
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback
} from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import {
  CallSplitOutlined,
  Sync as SyncIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import Loader from 'src/common/Loader';
import ReactECharts from 'src/common/ReactECharts';
import { APIService } from 'src/api/api';
import { getAllSites } from 'src/redux/siteSlice';
import { getFuelQuantity } from 'src/utils/getInitials';

// --- Professional "Fintech" Palette ---
const PROFESSIONAL_PALETTE = [
  '#2563EB', // Blue 600
  '#059669', // Emerald 600
  '#D97706', // Amber 600
  '#0B6E99', // Blue
  '#DB2777', // Pink 600
  '#0891B2', // Cyan 600
  '#DC2626', // Red 600
  '#2563EB', // Blue
  '#EA580C', // Orange 600
  '#65A30D', // Lime 600
  '#0D9488', // Teal 600
  '#1D4ED8' // Blue
];

const formatConsumptionValue = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return value;
  }

  return numericValue.toFixed(2);
};

// --- Chart Configuration Helper ---
const getChartOption = (data, theme) => {
  if (!data || !data.datasets || data.datasets.length === 0) return null;

  // Map API datasets to ECharts series (Horizontal Bars)
  const series = data.datasets.map((dataset, index) => {
    // Cycle through the professional palette
    const color = PROFESSIONAL_PALETTE[index % PROFESSIONAL_PALETTE.length];

    return {
      name: dataset.label,
      type: 'bar',
      stack: 'total',
      barWidth: 24, // Thicker, premium feel
      data: dataset.data,
      itemStyle: {
        color,
        borderColor: 'transparent',
        borderWidth: 0,
        borderRadius: 0
      },
      emphasis: {
        focus: 'series',
        itemStyle: {
          shadowBlur: 10,
          shadowColor: alpha(color, 0.4)
        }
      }
    };
  });

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => params.map((item) => {
        const dataset = data.datasets[item.seriesIndex];
        const gallons = dataset?.data_gallons?.[item.dataIndex];
        return `${item.marker}${item.seriesName}: ${getFuelQuantity(item.value, gallons ?? undefined)}`;
      }).join('<br/>'),
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      textStyle: {
        color: theme.palette.text.primary,
        fontSize: 12,
        fontFamily: theme.typography.fontFamily
      },
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: alpha(theme.palette.primary.main, 0.05)
        }
      },
      valueFormatter: (value) => formatConsumptionValue(value),
      order: 'valueDesc' // Sorts tooltip so highest volume is top
    },
    legend: {
      type: 'scroll',
      orient: 'horizontal',
      bottom: 0,
      left: 'center',
      width: '95%',
      itemGap: 20,
      icon: 'circle',
      textStyle: {
        color: theme.palette.text.secondary,
        fontSize: 11,
        fontWeight: 500
      },
      pageIconColor: theme.palette.text.secondary,
      pageIconInactiveColor: theme.palette.action.disabled,
      pageTextStyle: {
        color: theme.palette.text.secondary
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%',
      top: '5%',
      containLabel: true
    },
    // X-Axis = Volume (Value)
    xAxis: {
      type: 'value',
      position: 'top', // Move labels to top for cleaner look
      axisLabel: {
        formatter: (value) => formatConsumptionValue(value),
        color: theme.palette.text.secondary,
        fontSize: 11,
        fontWeight: 500
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: theme.palette.divider
        }
      }
    },
    // Y-Axis = Dates (Category)
    yAxis: {
      type: 'category',
      data: data.labels,
      inverse: true, // Top to bottom ordering (Jan 30 -> Feb 08)
      axisLabel: {
        color: theme.palette.text.primary,
        fontSize: 12,
        fontWeight: 600,
        width: 90,
        overflow: 'break'
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      }
    },
    series
  };
};

const ConsumptionChart = (props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { allSites } = useSelector((state) => state.sites);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [selectedSite, setSelectedSite] = useState('all');
  const isMountedRef = useRef(false);
  const requestIdRef = useRef(0);

  const siteData = useMemo(() => [
    { id: 'all', name: 'All Sites' },
    ...(allSites || [])
  ], [allSites]);

  const fetchChartData = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (isMountedRef.current) {
      setLoading(true);
    }

    try {
      const siteId = selectedSite === 'all' ? '' : selectedSite;
      const res = await APIService.getTerminalWiseFuelDispensed(siteId);

      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }

      if (!res || !res.success) {
        setChartData(null);
        return;
      }

      if (res.result && res.result.data) {
        setChartData(res.result.data);
      } else {
        setChartData(null);
      }
    } catch (error) {
      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }

      console.error('Terminal-wise consumption error:', error.message);
      setChartData(null);
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [selectedSite]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  useEffect(() => {
    if (!allSites || allSites.length === 0) {
      dispatch(getAllSites());
    }
  }, [dispatch, allSites]);

  const handleSiteChange = (e) => {
    setSelectedSite(e.target.value);
  };

  const chartOption = useMemo(() => (
    getChartOption(chartData, theme)
  ), [chartData, theme]);

  // Helper function to render chart content
  const renderChartContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Loader />
        </Box>
      );
    }

    if (chartOption) {
      return (
        <Fade
          in
          timeout={600}
        >
          <Box
            sx={{
              height: '100%',
              width: '100%',
              p: 2
            }}
          >
            <ReactECharts
              options={chartOption}
              className="csp-echarts csp-echarts--full"
            />
          </Box>
        </Fade>
      );
    }

    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          gap: 1
        }}
      >
        <CallSplitOutlined
          sx={{
            fontSize: 48,
            opacity: 0.2
          }}
        />
        <Typography variant="body2">
          No consumption data found
        </Typography>
      </Box>
    );
  };

  return (
    <Card
      {...props}
      sx={{
        mt: 0,
        width: '100%',
        height: '100%',
        borderRadius: 3,
        boxShadow: '0 4px 24px 0 rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <CardHeader
        sx={{
          p: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
        title={(
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CallSplitOutlined fontSize="medium" />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: 'text.primary'
                }}
              >
                TERMINAL CONSUMPTION
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Dispenser-wise breakdown
              </Typography>
            </Box>
          </Stack>
        )}
        action={(
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <Select
              name="site"
              variant="outlined"
              size="small"
              onChange={handleSiteChange}
              value={selectedSite}
              sx={{
                minWidth: 140,
                height: 36,
                bgcolor: '#F9FAFB',
                borderRadius: 2,
                fontSize: '0.813rem',
                fontWeight: 500,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.08)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main'
                }
              }}
            >
              {siteData.map((item) => (
                <MenuItem
                  value={item.id}
                  key={item.id}
                >
                  {item.name}
                </MenuItem>
              ))}
            </Select>

            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchChartData}
                size="small"
                sx={{
                  bgcolor: '#F9FAFB',
                  border: '1px solid',
                  borderColor: 'rgba(0,0,0,0.08)',
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                  color: 'text.secondary',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    transform: 'rotate(180deg)'
                  }
                }}
              >
                <SyncIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      />

      <CardContent
        sx={{
          p: 0,
          flex: 1,
          '&:last-child': {
            pb: 0
          }
        }}
      >
        <Box
          sx={{
            height: 480,
            width: '100%',
            position: 'relative'
          }}
        >
          {renderChartContent()}
        </Box>
      </CardContent>
    </Card>
  );
};

export default memo(ConsumptionChart);
